import { deepEquals, readField } from "./objectUtilities";

export const chunkArray = (array, chunkSize = 4) => {
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
    }
    return result;
}

export const chunkArrayIntoColumns = (arr, numOfColumns = 4) => {
    const columnLength = Math.ceil(arr.length / numOfColumns);
    return Array.from({ length: columnLength }, (_, row) =>
        Array.from({ length: numOfColumns }, (_, col) => {
            const index = col * columnLength + row;
            return index < arr.length ? arr[index] : undefined;
        }).filter(item => item !== undefined)
    );
}

export const transposeArray = (array) => {
    const maxLength = Math.max.apply(null, array.map(subarray => subarray.length));

    const result = Array.from({ length: maxLength }, (_, i) =>
        array.map(subarray => subarray[i] !== undefined ? subarray[i] : null)
    );

    return result;
}

export const groupBy = (array, field) => {
    return array.reduce((acc, obj) => {
        const key = readField(obj, field);
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
    }, {});
}

export const distinct = (array, field) => {
    if (!field) {
        return Array.from(new Set(array));
    } else if (!Array.isArray(field)) {
        const map = new Map();
        array.forEach(entry => map.set(readField(entry, field), entry));
        return Array.from(map.values());
    }
    return array.filter((a, i) => array.findIndex(a2 => field.every(f => readField(a, f) === readField(a2, f))) === i);
}

export const intersect = (array1, array2, equality = deepEquals) => {
    if (!array1 || !array2) {
        return [];
    }
    return array1.filter(elem1 => array2.some(elem2 => equality(elem1, elem2)));
}

export const ensureArray = (item) => {
    if (!item) {
        return [];
    } else if (Array.isArray(item)) {
        return item;
    } else {
        return [item];
    }
}

export const computeAdditionsAndRemovals = (A, B) => {
    // Convert arrays to sets
    let setA = new Set(A);
    let setB = new Set(B);

    // Compute A / B (elements in A not in B)
    let removed = new Set();
    for (let elem of setA) {
        if (!setB.has(elem)) {
            removed.add(elem);
        }
    }

    // Compute B / A (elements in B not in A)
    let added = new Set();
    for (let elem of setB) {
        if (!setA.has(elem)) {
            added.add(elem);
        }
    }

    // Return the results as an object with each result as a Set
    return {
        removed,
        added
    };
}

// Helper function to check if two arrays have the same elements in the same order
export const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
    }
    return true;
}

export const customArraysEqual = equals => (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        if (!equals(arr1[i], arr2[i])) return false;
    }
    return true;
}

export function syncObjectDraftWithCurrent(draft, current, equals = arraysEqual) {
    const result = {};

    // Create an empty Set for all unique keys
    const allKeys = new Set();
    const modifications = new Map();

    // Add keys from draft to the Set
    for (const key in current) {
        allKeys.add(key);
    }

    // Add keys from current to the Set
    for (const key in draft) {
        allKeys.add(key);
    }

    // Iterate over each unique key
    allKeys.forEach(key => {
        // Case a: Key exists in draft but not in current - do not add to result
        if (key in draft && !(key in current)) {
            modifications.set(key, { old: draft[key], new: null });
        }
        // Case b: Key exists in current but not in draft - add the key to draft with value from current
        else if (!(key in draft) && key in current) {
            modifications.set(key, { old: null, new: current[key] });
            result[key] = current[key];
        }
        // Case c: Key exists in both - check if the arrays have the same elements
        else if (!equals(draft[key], current[key])) {
            modifications.set(key, { old: draft[key], new: current[key] })
            result[key] = current[key];
        }
        else {
            result[key] = draft[key];
        }
    });
    return { result: modifications.size > 0 ? result : draft, modifications };
}

export function mergeRanges(ranges, { start, end } = { start: 'start', end: 'end' }) {
    ranges = [...ranges];
    // Sort by start
    ranges.sort((a, b) => a.start - b.start);
  
    const merged = [];
    for (const range of ranges) {
      // If merged array is empty, just push
      if (!merged.length) {
        merged.push({ [start]: range[start], [end]: range[end] });
        continue;
      }
  
      // Compare current range with the last merged range
      const last = merged[merged.length - 1];
      if (range[start] <= last[end]) {
        // Overlaps or touches, so merge by extending the end if needed
        last.end = Math.max(last[end], range[end]);
      } else {
        // No overlap, push a new entry
        merged.push({ [start]: range[start], [end]: range[end] });
      }
    }
  
    return merged;
  }
  